import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import { BLOG_CATEGORIES, BLOG_PATH } from "@/lib/blogConfig";
import { ArrowUpRight, Clock, Search } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [postCategories, setPostCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  // Merge preset categories (always visible) with any custom categories that
  // already have published posts. Preset order is preserved; extras go at the end.
  const categories = useMemo(() => {
    const extras = postCategories.filter((c) => !BLOG_CATEGORIES.includes(c));
    return [...BLOG_CATEGORIES, ...extras];
  }, [postCategories]);

  useEffect(() => {
    document.title = "Emerald Coast Real Estate Blog — J.J. Botelho | Florida";
    return () => {
      document.title = "J.J. Botelho — Realtor & Mortgage Advisor | Okaloosa County, FL";
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: postsData }, { data: catsData }] = await Promise.all([
          axios.get(`${API}/posts?limit=100`),
          axios.get(`${API}/posts/categories`),
        ]);
        if (!cancelled) {
          setPosts(Array.isArray(postsData) ? postsData : []);
          setPostCategories(Array.isArray(catsData) ? catsData : []);
        }
      } catch (e) {
        if (!cancelled) setPosts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = posts.filter((p) => {
    if (activeCat !== "All" && p.category !== activeCat) return false;
    if (q.trim()) {
      const needle = q.toLowerCase();
      return (
        p.title?.toLowerCase().includes(needle) ||
        p.excerpt?.toLowerCase().includes(needle) ||
        (p.tags || []).some((t) => t.toLowerCase().includes(needle))
      );
    }
    return true;
  });

  return (
    <main data-testid="blog-page" className="bg-bone min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-36 pb-16 md:pt-44 md:pb-20 bg-ink text-white overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-brass/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-12">
          <p className="overline text-brass">The Journal</p>
          <h1 className="mt-6 font-display text-white text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
            Notes from the
            <br />
            <span className="italic text-brass">Emerald Coast.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-white/75 text-base md:text-lg leading-relaxed">
            Market updates, buying & selling guides, mortgage strategy, Florida-specific
            insurance and tax notes, and a few hidden local gems — fresh perspectives
            from your dual-licensed Emerald Coast advisor.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-sand border-b border-border2 sticky top-20 z-30 backdrop-blur-md bg-sand/85">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 md:py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
          <div className="flex flex-nowrap md:flex-wrap gap-2 overflow-x-auto no-scrollbar flex-1">
            {["All", ...categories].map((c) => {
              const active = activeCat === c;
              return (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  data-testid={`blog-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex-shrink-0 whitespace-nowrap text-[11px] tracking-widest uppercase rounded-full px-3 py-1.5 md:px-4 md:py-2 border transition-colors duration-200 ${
                    active
                      ? "bg-ink text-white border-ink"
                      : "bg-white/70 text-text2 border-border2 hover:border-ink hover:text-ink"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>
          <div className="relative md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted2" />
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search posts…"
              data-testid="blog-search"
              className="w-full bg-white border border-border2 rounded-full pl-9 pr-4 py-2 text-sm focus:outline-none focus:border-ink"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-center text-muted2 text-sm">Loading posts…</p>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20" data-testid="blog-empty">
              <p className="overline text-ink justify-center">Coming Soon</p>
              <h2 className="mt-6 font-display text-ink text-3xl sm:text-4xl tracking-tight">
                {posts.length === 0
                  ? "First post on the way."
                  : "No posts in this category yet."}
              </h2>
              <p className="mt-4 text-text2/80 max-w-md mx-auto">
                {posts.length === 0
                  ? "J.J. publishes new posts weekly. Check back soon — or scroll down for ways to get in touch."
                  : "Try another category or clear the search."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filtered.map((post) => (
                <BlogCard key={post.id} post={post} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function BlogCard({ post }) {
  return (
    <Link
      to={`${BLOG_PATH}/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group block bg-white border border-border2 rounded-2xl overflow-hidden hover:border-brass/60 hover:shadow-lg transition-all duration-300"
    >
      <div className="relative aspect-[16/10] bg-sand overflow-hidden">
        {post.featured_image ? (
          <img
            src={post.featured_image}
            alt={post.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-ink to-slate2 flex items-center justify-center">
            <span className="font-display italic text-brass text-2xl">
              J.J.
            </span>
          </div>
        )}
        <div className="absolute top-3 left-3">
          <span className="text-[10px] tracking-widest uppercase bg-white/90 text-ink rounded-full px-3 py-1 font-medium backdrop-blur-sm">
            {post.category}
          </span>
        </div>
      </div>
      <div className="p-6">
        <h3 className="font-display text-ink text-xl md:text-2xl leading-tight tracking-tight">
          {post.title}
        </h3>
        {post.excerpt && (
          <p className="mt-3 text-text2/80 text-sm leading-relaxed line-clamp-3">
            {post.excerpt}
          </p>
        )}
        <div className="mt-5 flex items-center justify-between text-xs text-muted2">
          <span>{formatDate(post.published_at)}</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3.5 h-3.5" />
            {post.read_time_minutes} min
          </span>
        </div>
        <span className="mt-5 inline-flex items-center gap-1 text-sm text-ink font-medium border-b border-ink pb-0.5 group-hover:text-brass group-hover:border-brass transition-colors">
          Read
          <ArrowUpRight className="w-4 h-4" />
        </span>
      </div>
    </Link>
  );
}
